<template>
    <div>
        <Notifications v-if="currentUser" />
        <CurrencyConverter />
        <Welcome v-if="currentUser && !currentUser.accepted_terms" />
        <AuditsModal v-if="currentUser?.is_admin === true" />
        <!-- Account specific Components -->
        <template v-if="currentAccount">
            <Subcategory />
            <Supplier />
            <SendEmail />
            <ModalCreateTaxRate />
            <PurchaseOrder />
            <OCRDocumentUpload />
            <OCRInvoiceEditor />
            <RevenueEntry />
            <BudgetEntry />
            <Department />
            <ViewInvoice />
            <RaisePurchaseInvoice />
            <ModalCreateSupplier v-if="Auth.can('manage suppliers')" object-label="Contact" />
            <EventWizard v-if="Auth.can('create events')" :key="currentAccount.id" />
        </template>
        <!-- Event specific Components -->
        <template v-if="currentEvent">
            <CreateBudgetEntry />
            <RechargeBudgetEntry />
            <CreateRevenueEntry />
            <ModalBankTransactions />
            <RaiseMultiPurchaseInvoice />
            <GlobalSearch v-if="currentEvent && !currentAccount.is_supplier" class="tw-mx-auto" />
        </template>
        <Toast />
        <CreateTickets
            v-if="currentAccount && currentAccount.features && currentAccount.features.ticketing && currentEvent"
        />
    </div>
</template>
<script setup lang="ts">
import { useDataStore } from '@/js/stores/DataStore.js';
import { useSettingsStore } from '@/js/stores/SettingsStore.js';
import { mentionables } from '@/js/mentionables.js';
import { Auth, route } from '@/js/utils.js';
import { Eventbus } from '@/js/utils/eventbus.ts';
import Notifications from '@/assets/js/eventbooks/components/utility/notifications.vue';
import CurrencyConverter from '@/js/components/widgets/CurrencyConverter.vue';
import Subcategory from '@/js/components/drawers/Category.vue';
import Supplier from '@/js/components/drawers/Supplier.vue';
import Welcome from '@/js/components/modals/Welcome.vue';
import AuditsModal from '@/js/components/modals/AuditsModal.vue';
import CreateBudgetEntry from '@/js/components/drawers/CreateBudgetEntry.vue';
import PurchaseOrder from '@/js/components/PurchaseOrder.vue';
import OCRDocumentUpload from '@/js/components/drawers/OCRDocumentUpload.vue';
import BudgetEntry from '@/js/components/drawers/BudgetEntry.vue';
import CreateRevenueEntry from '@/js/components/drawers/NewRevenueEntry.vue';
import RevenueEntry from '@/js/components/drawers/Revenue/RevenueEntry.vue';
import ModalCreateTaxRate from '@/js/components/modals/TaxRate/CreateTaxRate.vue';
import ModalBankTransactions from '@/assets/js/eventbooks/components/bank-transactions/modal.vue';
import RechargeBudgetEntry from '@/js/components/modals/RechargeBudgetEntry.vue';
import RaiseMultiPurchaseInvoice from '@/js/components/modals/Invoices/RaiseMultiPurchaseInvoice.vue';
import ViewInvoice from '@/js/components/modals/Invoices/ViewInvoice.vue';
import GlobalSearch from '@/js/components/widgets/GlobalSearch.vue';
import Department from '@/js/components/drawers/Department.vue';
import Toast from '@/js/components/Toast.vue';
import OCRInvoiceEditor from '@/js/components/drawers/OCRInvoiceEditor.vue';
import ModalCreateSupplier from '@/js/components/modals/Suppliers/CreateSupplier.vue';
import RaisePurchaseInvoice from '@/js/components/modals/Invoices/RaisePurchaseInvoice.vue';
import CreateTickets from '@/js/components/modals/Ticketing/CreateTickets.vue';
import SendEmail from '@/js/components/modals/SendEmail.vue';
import EventWizard from '@/js/components/EventWizard.vue';
import { computed, onMounted, onUnmounted, ref } from 'vue';

const store = useDataStore();
const settings = useSettingsStore();
const currentRoute = ref(route().current());

const currentAccount = computed(() => store.get('account'));
const currentEvent = computed(() => store.get('current_event'));
const currentUser = computed(() => store.get('current_user'));

function handleRouteChanged(): void {
    currentRoute.value = route().current();
}

onMounted(() => {
    Eventbus.$on('routeChanged', handleRouteChanged);

    if (currentEvent.value) {
        mentionables.initialise();
    }
});

onUnmounted(() => {
    Eventbus.$off('routeChanged', handleRouteChanged);
});

defineExpose({
    store,
    settings,
    currentRoute,
});
</script>
